/**
 * "Editar" uma consulta da lista (#1011, protótipo do Claude Design).
 *
 * Muda especialidade e médico. Marcada no SISAC, a data e hora e o código da
 * agenda aparecem só para leitura, e o aviso diz que o que mudar aqui fica só no
 * CRM. Ainda não marcada, a data e hora se digitam, como a aba sempre permitiu.
 *
 * Só tela: quem a monta passa a consulta e as opções, e decide o que "Salvar"
 * faz (ver `lib/consultas-na-tela.ts`).
 */

import { useState, type ReactNode } from 'react';
import { Botao, Campo, CampoSelecao, Ficha, Selo, type LinhaFicha } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  opcoesComAAtual,
  type ConsultaNaLista,
  type EstadoDoEnvio,
  type OpcaoDaAgenda,
} from '../lib/consultas-na-tela';
import type { EscolhaDoMedico } from './nova-consulta';
import './editar-consulta.css';

function linha(rotulo: string, valor: string | null): LinhaFicha {
  return valor === null ? { rotulo, valor: 'não informado', vazio: true } : { rotulo, valor };
}

/** O que "Salvar" devolve. Vazio é sem escolha; `horario` só vem da consulta não marcada. */
export type EdicaoDaConsulta = EscolhaDoMedico & { readonly horario?: string };

export function EditarConsulta({
  consulta,
  especialidades,
  medicos,
  horario,
  envio,
  aoSalvar,
  aoCancelar,
  aoRecarregar,
}: {
  consulta: ConsultaNaLista;
  especialidades: readonly OpcaoDaAgenda[];
  medicos: readonly OpcaoDaAgenda[];
  /** O valor do campo "Data e hora" (`datetime-local`), só para a consulta não marcada. */
  horario?: string;
  envio: EstadoDoEnvio;
  aoSalvar: (edicao: EdicaoDaConsulta) => void;
  aoCancelar: () => void;
  /** Outra pessoa salvou antes: reler a consulta e fechar a edição. */
  aoRecarregar: () => void;
}): ReactNode {
  const [especialidadeId, definirEspecialidadeId] = useState(consulta.especialidade?.id ?? '');
  const [medicoId, definirMedicoId] = useState(consulta.medico?.id ?? '');
  const horarioEditavel = !consulta.marcada && horario !== undefined;
  const [campoDoHorario, definirCampoDoHorario] = useState(horario ?? '');
  // Marcada, a consulta tem médico no SISAC: salvar sem ele apagaria quem atende.
  const faltaMedico = consulta.marcada && !medicoId;
  const salvando = envio.situacao === 'enviando';
  const listaDeEspecialidades = opcoesComAAtual(especialidades, consulta.especialidade);
  const listaDeMedicos = opcoesComAAtual(medicos, consulta.medico);
  const quando = consulta.quando
    ? consulta.marcada
      ? `${consulta.quando} (marcada no SISAC)`
      : consulta.quando
    : null;

  return (
    <form
      className="editar-consulta"
      aria-labelledby="editar-consulta-titulo"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (faltaMedico || salvando) return;
        aoSalvar(
          horarioEditavel
            ? { especialidadeId, medicoId, horario: campoDoHorario }
            : { especialidadeId, medicoId },
        );
      }}
    >
      <div className="editar-consulta__topo">
        <h3 className="editar-consulta__titulo" id="editar-consulta-titulo">
          Editar consulta
        </h3>
        {consulta.marcada && <Selo tom="ok">Marcada no SISAC</Selo>}
      </div>

      {consulta.marcada && (
        <Faixa tom="atencao">
          Esta consulta já está marcada no SISAC. O que mudar aqui fica só no CRM: a marcação no
          SISAC continua como está.
        </Faixa>
      )}

      {listaDeMedicos.length === 0 && listaDeEspecialidades.length === 0 && (
        <p className="editar-consulta__nota" role="status">
          A lista de médicos e especialidades vem do SISAC e ainda está vazia. Ela aparece aqui
          assim que a integração for ligada.
        </p>
      )}

      <div className="editar-consulta__dupla">
        <CampoSelecao
          id="editar-consulta-especialidade"
          rotulo="Especialidade"
          value={especialidadeId}
          onChange={(e) => definirEspecialidadeId(e.currentTarget.value)}
        >
          <option value="">Escolha</option>
          {listaDeEspecialidades.map((opcao) => (
            <option key={opcao.id} value={opcao.id}>
              {opcao.nome}
            </option>
          ))}
        </CampoSelecao>
        <CampoSelecao
          id="editar-consulta-medico"
          rotulo="Médico"
          value={medicoId}
          onChange={(e) => definirMedicoId(e.currentTarget.value)}
        >
          <option value="">Escolha</option>
          {listaDeMedicos.map((opcao) => (
            <option key={opcao.id} value={opcao.id}>
              {opcao.nome}
            </option>
          ))}
        </CampoSelecao>
      </div>

      {horarioEditavel ? (
        <Campo
          id="editar-consulta-horario"
          rotulo="Data e hora"
          type="datetime-local"
          value={campoDoHorario}
          onChange={(e) => definirCampoDoHorario(e.currentTarget.value)}
        />
      ) : (
        <Ficha
          larguraRotulo="148px"
          linhas={[linha('Data e hora', quando), linha('Agenda no SISAC', consulta.codigoDaAgenda)]}
        />
      )}

      {envio.situacao === 'conflito' && (
        <p className="editar-consulta__erro" role="alert">
          Outra pessoa, ou o SISAC, mudou esta consulta enquanto você editava. Recarregue para ver a
          versão atual e refaça a sua alteração.
        </p>
      )}
      {envio.situacao === 'erro' && (
        <p className="editar-consulta__erro" role="alert">
          {envio.recado}
        </p>
      )}

      <div className="editar-consulta__acoes">
        <Botao variante="fantasma" type="button" onClick={aoCancelar}>
          Cancelar
        </Botao>
        {envio.situacao === 'conflito' ? (
          <Botao variante="primario" type="button" onClick={aoRecarregar}>
            Recarregar a consulta
          </Botao>
        ) : (
          <Botao
            variante="primario"
            type="submit"
            desabilitado={faltaMedico || salvando}
            motivo={salvando ? 'Salvando…' : 'Escolha o médico.'}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Botao>
        )}
      </div>
    </form>
  );
}
